// frontend/src/pages/Socrates/components/TemplateManagement/TemplateDetail/index.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlobe, faUsers, faCalendarAlt, 
  faCheckCircle, faTimesCircle, faCog, 
  faEdit, faTrash, faExclamationTriangle,
  faInfoCircle, faCopy, faCheck, faLock,
  faSliders, faFire, faUserFriends, faQuoteRight, 
  faEye, faBrain, faSitemap, faPalette, faComments,
  faShield, faExclamationCircle, faFilter, faBan,
  faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useTemplate } from '../../../contexts/TemplateContext';
import { useSocrates } from '../../../contexts/SocratesContext';
import SocratesModal from '../../common/SocratesModal';
import LoadingSpinner from '../../common/LoadingSpinner';
import styles from './TemplateDetail.module.css';

// Parameter icons mapping - Updated with safety parameters
const PARAMETER_ICONS = {
  'token_count': faSliders,
  'creativity_level': faFire,
  'audience_options': faUserFriends,
  'citation_styles': faQuoteRight,
  'perspective_options': faEye,
  'purpose_options': faCog,
  'reasoning_types': faBrain,
  'structure_options': faSitemap,
  'style_options': faPalette,
  'tone_options': faComments,
  'content_filtering_level': faFilter,
  'content_warning_settings': faExclamationCircle,
  'refusal_strategies': faBan,
  'hallucination_controls': faShield,
  'prohibited_topic_categories': faExclamationTriangle,
  'harm_monitoring_categories': faShield
};

const TemplateDetail = ({ template, onClose }) => {
  const { t } = useLanguage();
  const { 
    openEditForm, 
    deleteTemplate,
    refreshTemplates
  } = useTemplate();
  const { isSuperAdmin } = useSocrates();

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    core: true, // First category expanded by default
    communication: false,
    safety: false,
    other: false
  });

  if (!template) {
    return (
      <div className={styles.emptyState}>
        <FontAwesomeIcon icon={faInfoCircle} className={styles.emptyIcon} />
        <h3 className={styles.emptyTitle}>{t('no_template_selected')}</h3>
        <p className={styles.emptyDescription}>
          {t('select_template_to_view_details')}
        </p>
      </div>
    );
  }

  const isSystemTemplate = template.is_system_template === 'true';
  const isActive = template.is_active !== 'false';

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('not_available');
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Copy template ID to clipboard
  const copyTemplateId = async () => {
    try {
      await navigator.clipboard.writeText(template.template_id);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy template ID:', error);
    }
  };

  // Handle edit
  const handleEdit = () => {
    if (isSystemTemplate && !isSuperAdmin) {
      return; // Prevent editing for non-admin users
    }
    
    // Ensure the template is properly set before opening the edit form
    const templateToEdit = JSON.parse(JSON.stringify(template));

    // Close the detail modal first
    onClose();
    
    // Wait for the modal to close before opening the edit form
    setTimeout(() => {
      openEditForm(templateToEdit);
    }, 300);
  };

  // Handle delete
  const handleDelete = () => {
    if (isSystemTemplate && !isSuperAdmin) {
      return; // Prevent deletion for non-admin users
    }
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      const response = await deleteTemplate(template.template_id);
      
      if (response && response.success) {
        setDeleteSuccess(true);
        
        // Close modals after success
        setTimeout(() => {
          setDeleteConfirmOpen(false);
          // Wait a bit before closing the detail modal to show success message
          setTimeout(() => {
            onClose();
            // Refresh templates list
            refreshTemplates();
          }, 500);
        }, 1500);
      } else {
        throw new Error(response?.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      setDeleteError(error.message || 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteError(null);
  };

  // Format parameter name
  const formatParameterName = (name) => {
    if (!name) return '';
    
    // Remove common prefixes
    let formatted = name.replace(/^plato_/, '');
    
    // Convert snake_case to Title Case
    formatted = formatted
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return formatted;
  };

  // Get parameter value display
  const getParameterValueDisplay = (parameter) => {
    if (parameter.parameter_type === 'integer' || parameter.parameter_type === 'number') {
      return parameter.selected_value || parameter.default_value || 'N/A';
    }
    
    if (parameter.parameter_type === 'boolean') {
      const value = parameter.selected_value || parameter.default_value;
      return value === 'true' ? t('yes') : t('no');
    }
    
    // For object type parameters, use translation if available
    if (parameter.parameter_type === 'object') {
      const value = parameter.selected_value || parameter.default_value || 'system_default';
      const paramName = parameter.parameter_name.replace(/_options$/, '');
      const translationKey = `plato.parameters.${paramName}.options.${value}`;
      return t(translationKey, { defaultValue: value });
    }
    
    return parameter.selected_value || parameter.default_value || 'system_default';
  };

  // Get parameter icon
  const getParameterIcon = (parameterName) => {
    return PARAMETER_ICONS[parameterName] || faCog;
  };

  // Get parameter description
  const getParameterDescription = (parameter) => {
    const paramName = parameter.parameter_name.replace(/_options$/, '');
    const descriptionKey = `plato.parameters.${paramName}.detailed_ui_desc`;
    return t(descriptionKey, { defaultValue: '' });
  };

  // Get parameter label
  const getParameterLabel = (parameter) => {
    const paramName = parameter.parameter_name.replace(/_options$/, '');
    const labelKey = `plato.parameters.${paramName}.label`;
    return t(labelKey, { defaultValue: formatParameterName(parameter.parameter_name) });
  };

  // Toggle category expansion
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Categorize parameters for better organization
  const categorizeParameters = (parameters) => {
    const categories = {
      core: [],
      communication: [],
      safety: [],
      other: []
    };

    parameters.forEach(param => {
      const paramName = param.parameter_name;
      
      if (['token_count', 'creativity_level'].includes(paramName)) {
        categories.core.push(param);
      } else if (['audience_options', 'perspective_options', 'style_options', 'tone_options', 'structure_options'].includes(paramName)) {
        categories.communication.push(param);
      } else if (['content_filtering_level', 'content_warning_settings', 'refusal_strategies', 'hallucination_controls', 'prohibited_topic_categories', 'harm_monitoring_categories'].includes(paramName)) {
        categories.safety.push(param);
      } else {
        categories.other.push(param);
      }
    });

    return categories;
  };

  // Render parameter category section
  const renderParameterCategory = (categoryParams, categoryName, categoryIcon, categoryColor) => {
    if (categoryParams.length === 0) return null;

    const isExpanded = expandedCategories[categoryName];

    return (
      <div className={styles.parameterCategory} key={categoryName}>
        <div 
          className={styles.categoryHeader}
          onClick={() => toggleCategory(categoryName)}
        >
          <FontAwesomeIcon 
            icon={categoryIcon} 
            className={`${styles.categoryIcon} ${styles[categoryColor]}`} 
          />
          <h4 className={styles.categoryTitle}>{t(`parameter_category_${categoryName}`)}</h4>
          <span className={styles.categoryCount}>({categoryParams.length})</span>
          <FontAwesomeIcon 
            icon={isExpanded ? faChevronUp : faChevronDown} 
            className={styles.accordionIcon}
          />
        </div>
        {isExpanded && (
          <div className={styles.categoryParameters}>
            {categoryParams.map((parameter, index) => {
              const parameterIcon = getParameterIcon(parameter.parameter_name);
              const parameterDescription = getParameterDescription(parameter);
              const parameterLabel = getParameterLabel(parameter);
              
              return (
                <div key={`${parameter.parameter_id}-${index}`} className={styles.parameterCard}>
                  <div className={styles.parameterHeader}>
                    <FontAwesomeIcon icon={parameterIcon} className={styles.parameterIcon} />
                    <div className={styles.parameterTitle}>
                      <h3 className={styles.parameterName}>
                        {parameterLabel}
                      </h3>
                      <span className={styles.parameterType}>{parameter.parameter_type}</span>
                    </div>
                  </div>
                  
                  <div className={styles.parameterValue}>
                    <span className={styles.valueLabel}>{t('value')}:</span>
                    <span className={styles.valueDisplay}>
                      {getParameterValueDisplay(parameter)}
                    </span>
                  </div>
                  
                  {parameterDescription && (
                    <div className={styles.parameterDescription}>
                      <p>{parameterDescription}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const parameterCategories = template.parameters ? categorizeParameters(template.parameters) : { core: [], communication: [], safety: [], other: [] };

  return (
    <div className={styles.templateDetail}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.titleSection}>
            <div className={styles.titleRow}>
              <FontAwesomeIcon 
                icon={isSystemTemplate ? faGlobe : faUsers} 
                className={`${styles.titleIcon} ${isSystemTemplate ? styles.system : styles.user}`}
              />
              <h1 className={styles.title}>{template.template_name}</h1>
            </div>
            <div className={styles.templateMeta}>
              <span className={`${styles.typeBadge} ${isSystemTemplate ? styles.systemBadge : styles.userBadge}`}>
                {isSystemTemplate ? t('system_template') : t('user_template')}
              </span>
              <div className={`${styles.statusBadge} ${isActive ? styles.activeBadge : styles.inactiveBadge}`}>
                {isActive ? (
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.activeIcon} />
                ) : (
                  <FontAwesomeIcon icon={faTimesCircle} className={styles.inactiveIcon} />
                )}
                <span>{isActive ? t('active') : t('inactive')}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.headerActions}>
          <button 
            className={`${styles.editButton} ${isSystemTemplate && !isSuperAdmin ? styles.disabled : ''}`}
            onClick={handleEdit}
            title={isSystemTemplate && !isSuperAdmin ? t('requires_super_admin_role') : t('edit_template')}
            disabled={isSystemTemplate && !isSuperAdmin}
          >
            <FontAwesomeIcon icon={faEdit} />
            <span>{t('edit')}</span>
          </button>
          <button 
            className={`${styles.deleteButton} ${isSystemTemplate && !isSuperAdmin ? styles.disabled : ''}`}
            onClick={handleDelete}
            title={isSystemTemplate && !isSuperAdmin ? t('requires_super_admin_role') : t('delete_template')}
            disabled={isSystemTemplate && !isSuperAdmin}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>{t('delete')}</span>
          </button>
        </div>
        
        {/* Show admin-only message for system templates if not super admin */}
        {isSystemTemplate && !isSuperAdmin && (
          <div className={styles.adminOnlyBadge} title={t('requires_super_admin_role')}>
            <FontAwesomeIcon icon={faLock} />
            <span>{t('admin_only')}</span>
          </div>
        )}
      </div>

      {/* Template ID Section */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('template_identification')}</h2>
        <div className={styles.idSection}>
          <div className={styles.idField}>
            <label className={styles.fieldLabel}>{t('template_id')}</label>
            <div className={styles.idValue}>
              <code className={styles.idCode}>{template.template_id}</code>
              <button 
                className={`${styles.copyButton} ${copySuccess ? styles.copySuccess : ''}`}
                onClick={copyTemplateId}
                title={copySuccess ? t('copied') : t('copy_template_id')}
              >
                <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('description')}</h2>
        <div className={styles.descriptionContent}>
          {template.template_description ? (
            <p className={styles.description}>{template.template_description}</p>
          ) : (
            <p className={styles.noDescription}>{t('no_description_provided')}</p>
          )}
        </div>
      </div>

      {/* Parameters Section */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <FontAwesomeIcon icon={faCog} className={styles.sectionIcon} />
          {t('parameters')} ({template.parameters?.length || 0})
        </h2>
        
        {!template.parameters || template.parameters.length === 0 ? (
          <div className={styles.emptyParameters}>
            <FontAwesomeIcon icon={faCog} className={styles.emptyIcon} />
            <p>{t('no_parameters_configured')}</p>
          </div>
        ) : (
          <div className={styles.parametersContainer}>
            {/* Core Parameters */}
            {renderParameterCategory(
              parameterCategories.core, 
              'core', 
              faSliders, 
              'coreColor'
            )}

            {/* Communication Parameters */}
            {renderParameterCategory(
              parameterCategories.communication, 
              'communication', 
              faComments, 
              'communicationColor'
            )}

            {/* Safety Parameters */}
            {renderParameterCategory(
              parameterCategories.safety, 
              'safety', 
              faShield, 
              'safetyColor'
            )}

            {/* Other Parameters */}
            {renderParameterCategory(
              parameterCategories.other, 
              'other', 
              faCog, 
              'otherColor'
            )}
          </div>
        )}
      </div>

      {/* Metadata Section */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('metadata')}</h2>
        <div className={styles.metadataGrid}>
          <div className={styles.metadataItem}>
            <FontAwesomeIcon icon={faCalendarAlt} className={styles.metadataIcon} />
            <div className={styles.metadataContent}>
              <span className={styles.metadataLabel}>{t('created_at')}</span>
              <span className={styles.metadataValue}>{formatDate(template.created_at)}</span>
            </div>
          </div>
          
          <div className={styles.metadataItem}>
            <FontAwesomeIcon icon={faCalendarAlt} className={styles.metadataIcon} />
            <div className={styles.metadataContent}>
              <span className={styles.metadataLabel}>{t('updated_at')}</span>
              <span className={styles.metadataValue}>{formatDate(template.updated_at)}</span>
            </div>
          </div>
          
          {template.created_by && (
            <div className={styles.metadataItem}>
              <FontAwesomeIcon icon={faUsers} className={styles.metadataIcon} />
              <div className={styles.metadataContent}>
                <span className={styles.metadataLabel}>{t('created_by')}</span>
                <span className={styles.metadataValue}>{template.created_by}</span>
              </div>
            </div>
          )}
          
          {template.updated_by && (
            <div className={styles.metadataItem}>
              <FontAwesomeIcon icon={faUsers} className={styles.metadataIcon} />
              <div className={styles.metadataContent}>
                <span className={styles.metadataLabel}>{t('updated_by')}</span>
                <span className={styles.metadataValue}>{template.updated_by}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* System Template Warning */}
      {isSystemTemplate && (
        <div className={styles.systemWarning}>
          <FontAwesomeIcon icon={faInfoCircle} className={styles.warningIcon} />
          <div className={styles.warningContent}>
            <h3 className={styles.warningTitle}>{t('system_template_notice')}</h3>
            <p className={styles.warningText}>
              {t('system_template_notice_description')}
            </p>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <SocratesModal
        isOpen={deleteConfirmOpen}
        onClose={cancelDelete}
        title={t('confirm_delete')}
        size="small"
      >
        <div className={styles.deleteConfirmation}>
          {isDeleting ? (
            <div className={styles.deleteLoading}>
              <LoadingSpinner size="medium" />
              <p>{t('deleting_template')}</p>
            </div>
          ) : deleteSuccess ? (
            <div className={styles.deleteSuccess}>
              <div className={styles.successIcon}>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <h3 className={styles.successTitle}>{t('template_deleted')}</h3>
              <p className={styles.successMessage}>{t('template_deleted_successfully')}</p>
            </div>
          ) : (
            <>
              <div className={styles.warningIcon}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>

              <p className={styles.confirmMessage}>
                {t('delete_template_warning', { 
                  templateName: template.template_name 
                })}
              </p>
              {deleteError && (
                <div className={styles.deleteError}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{deleteError}</span>
                </div>
              )}
              <div className={styles.confirmActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={cancelDelete}
                >
                  {t('cancel')}
                </button>
                <button 
                  className={styles.confirmDeleteButton}
                  onClick={confirmDelete}
                >
                  {t('delete')}
                </button>
              </div>
            </>
          )}
        </div>
      </SocratesModal>
    </div>
  );
};

export default TemplateDetail;